import { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import { t } from 'i18next'

import { formatDateToRequest, getWhiteLabelPublisherId } from '~/helpers'
import { getCampaign } from '~/modules/retailMedia/store/campaign/actions'
import { patchNetworkDestinationUrls } from '~/modules/retailMedia/store/industry/campaign/actions'
import { useAppSelector } from '~/store/hooks'

import { getDestinationName } from '../../helpers/getDestinationName'
import DestinationUrlsEditor, {
  DestinationUrlEditorEntry
} from '../DestinationUrlsEditor'

const findConfiguredName = (
  destinationsConfig: DestinationUrlsConfig[],
  { brand, publisher_id }: DestinationUrlEditorEntry
) => getDestinationName(destinationsConfig, publisher_id, brand) || brand

export const projectOwnApprovedDestinations = (
  savedDestinations: DestinationUrlEditorEntry[] = [],
  destinationsConfig: DestinationUrlsConfig[] = [],
  publisherId: string | null | undefined = getWhiteLabelPublisherId()
): DestinationUrlEditorEntry[] => {
  const ownSavedDestinations = savedDestinations.filter(
    destination =>
      !destination.publisher_id ||
      !publisherId ||
      destination.publisher_id === publisherId
  )
  const configuredDestinations = destinationsConfig.filter(
    destination =>
      destination.publisher_id === publisherId && !!destination.code
  )
  const configuredBrands = new Set(
    configuredDestinations.map(destination => destination.code)
  )
  const projected = configuredDestinations.map(destination => {
    const saved = ownSavedDestinations.find(
      item => item.brand === destination.code
    )

    return {
      url: saved?.url || '',
      brand: destination.code,
      name: destination.name || destination.code
    }
  })
  const preserved = ownSavedDestinations
    .filter(item => !item.brand || !configuredBrands.has(item.brand))
    .map(item => ({
      url: item.url || '',
      brand: item.brand,
      // The saved payload carries the brand code only, and the projection
      // above is empty whenever `publisherId` doesn't match the catalog —
      // so the label still has to be resolved by code, or the raw code
      // would surface as the field name.
      name: item.name || findConfiguredName(destinationsConfig, item)
    }))
  const entries = [...projected, ...preserved]

  if (!entries.length) return [{ url: '' }]

  return entries.sort((a, b) =>
    (a.name || a.brand || '').localeCompare(
      b.name || b.brand || '',
      undefined,
      {
        sensitivity: 'base'
      }
    )
  )
}

const OwnApprovedDestinationUrlsEditor = ({
  data,
  destinationsConfig,
  onCancel
}: {
  data: CampaignView
  destinationsConfig: DestinationUrlsConfig[]
  onCancel: () => void
}) => {
  const dispatch = useDispatch()
  const ownPublisherId =
    data.raw?.destination_urls?.find(item => item.publisher_id)?.publisher_id ||
    getWhiteLabelPublisherId()
  const initialEntries = useMemo(
    () =>
      projectOwnApprovedDestinations(
        data.raw?.destination_urls || [],
        destinationsConfig,
        ownPublisherId
      ),
    [data.raw?.destination_urls, destinationsConfig, ownPublisherId]
  )
  const [entries, setEntries] = useState(initialEntries)
  const { startDate, endDate } = useAppSelector(state => state.datepickerHeader)
  const loadingPatchKeys = useAppSelector(
    state => state.retailMedia.industry.campaign.patch.loadingKeys
  )
  const patchKey = 'destination_url'
  const isSaving = !!loadingPatchKeys?.includes(patchKey)

  const handleSuccess = useCallback(() => {
    dispatch(
      getCampaign({
        start_date: formatDateToRequest(startDate),
        end_date: formatDateToRequest(endDate),
        campaign_id: data.id,
        account_info: true,
        requestInBackground: true
      })
    )
    onCancel()
  }, [data.id, dispatch, endDate, onCancel, startDate])

  const handleSubmit = useCallback(() => {
    dispatch(
      patchNetworkDestinationUrls({
        body: {
          destination_urls: entries.map(({ url, brand }) =>
            brand ? { url, brand } : { url }
          )
        },
        id: data.id,
        patchKey,
        successFeedbackMsg: t('rm:campaign.successfully.updated'),
        onSuccess: handleSuccess
      })
    )
  }, [data.id, dispatch, entries, handleSuccess])

  return (
    <DestinationUrlsEditor
      entries={entries}
      onChange={(index, url) =>
        setEntries(current =>
          current.map((entry, entryIndex) =>
            entryIndex === index ? { ...entry, url } : entry
          )
        )
      }
      onCancel={onCancel}
      onSubmit={handleSubmit}
      isSaving={isSaving}
      showHelperText
    />
  )
}

export default OwnApprovedDestinationUrlsEditor
